// src/pages/NewAskKural/components/QuantCitationDrawer/index.tsx
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/store";
import { Button, CloseOutlined, message, Tooltip } from "src/assets/imports/antdComponents";
import { useFeatures } from "src/config/featureManager";
import { FeatureKeysType } from "src/pages/Studies/models/interfaces";
import { downloadQuantCitationPPT } from "../../serviceHelper";
import { IQuantCitationDrawerProps } from "../../models/interfaces";
import { transformFePayloadToChartResponse } from "../../transformer";
import DynamicChartRender from "src/pages/Insights/components/QuantDashboard/Charts/DynamicChartRender";
import "./style.css";
import { EllipsisText } from "src/pages/Insights/components/QuantDashboard/Charts/components/EllipsisText";
import { ChartRenderedFromType } from "src/pages/Insights/models/interfaces";

const QuantCitationDrawer: React.FC<IQuantCitationDrawerProps> = ({ quantCitationData, onClose, isExpanded, handleExpand }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { isFeatureAvailable } = useFeatures();
	const isPPTExportAvailable = isFeatureAvailable(FeatureKeysType.PptExport);
	const { userProfileId, role: userRole, selectedStudy } = useSelector((state: RootState) => state.studiesSlice);
	const [isChartView, setIsChartView] = useState<boolean>(true);
	const [isExporting, setIsExporting] = useState<boolean>(false);

	const handleExportPPT = useCallback(async () => {
		if (isExporting) return;
		if (!quantCitationData?.fe_payload) {
			message.warning(t("No chart data available to export"));
			return;
		}
		setIsExporting(true);
		const loadingMessage = message.loading(t("Downloading"), 1000);
		try {
			await downloadQuantCitationPPT(quantCitationData.fe_payload, userProfileId, userRole, dispatch, selectedStudy?.study_name);
		} catch (err) {
			console.error("Quant citation PPT export failed:", err);
			message.error(t("An unexpected error occurred"));
		} finally {
			loadingMessage();
			setIsExporting(false);
		}
	}, [isExporting, quantCitationData, userProfileId, userRole, dispatch, t]);

	// Transform fe_payload into chart-compatible format
	const chartResponse = useMemo(() => {
		if (!quantCitationData?.fe_payload) return null;
		return transformFePayloadToChartResponse(quantCitationData.fe_payload);
	}, [quantCitationData]);

	// Wrap as array for DynamicChartRender (it expects chartsResponse
	// to be an array of chart objects or { sections: [{ charts }] })
	const chartsResponse = useMemo(() => {
		if (!chartResponse) return [];
		return [chartResponse];
	}, [chartResponse]);

	const { footnote, ai_header, chart_title } = quantCitationData?.fe_payload ?? {};

	const { quesNo, questionText } = useMemo(() => {
		const quesNo = footnote?.src?.match(/^([a-zA-Z0-9_]+)\./)?.[1]?.trim();
		const questionText = footnote?.q_txt?.trim() ? footnote.q_txt.trim().replace(/\s_\s/g, " = ") : undefined;
		return { quesNo, questionText };
	}, [footnote?.src, footnote?.q_txt]);

	if (!quantCitationData || !chartResponse) {
		return (
			<div className="quant-citation-drawer">
				<div className="quant-citation-drawer-header bg-[--drawer-header-bg]">
					<span className="text-[--secondary-color] text-base-14 ml-1">{t("References")}</span>
					<CloseOutlined onClick={onClose} className="secondary-color p-2 text-base" />
				</div>
				<div className="quant-citation-drawer-empty">
					<span className="text-[--neutral-grey] text-base-1">{t("No chart data available")}</span>
				</div>
			</div>
		);
	}

	return (
		<div className="quant-citation-drawer">
			{/* Header */}
			<div className="quant-citation-drawer-header bg-[--drawer-header-bg]">
				<div className="flex items-center gap-2 flex-1 min-w-0">
					{chart_title ? (
						<div className="flex-1 min-w-0">
							<EllipsisText
								text={chart_title}
								maxLines={1}
								lineHeight={20}
								className="text-[--secondary-color] text-base-1 font-semibold"
								getPopupContainer={(triggerNode) => (triggerNode.closest(".quant-citation-drawer") as HTMLElement) || document.body}
							/>
						</div>
					) : (
						<span className="text-[--secondary-color] text-base-14 font-semibold">{t("References")}</span>
					)}
				</div>
				<div className="flex items-center gap-2">
					{isPPTExportAvailable && (
						<Tooltip title={t("Export PowerPoint")} placement="topRight">
							<Button
								type="text"
								onClick={handleExportPPT}
								disabled={isExporting}
								className="quant-citation-header-btn"
								icon={<i className="material-symbols-outlined text-[1.5rem] text-[--secondary-color]">ios_share</i>}
							/>
						</Tooltip>
					)}
					<i className="material-symbols-outlined text-[1.5rem] cursor-pointer text-[--secondary-color]" onClick={handleExpand}>
						{isExpanded ? "collapse_content" : "expand_content"}
					</i>
					<CloseOutlined onClick={onClose} className="secondary-color p-2 text-base" />
				</div>
			</div>

			{/* Chart Area */}
			<div className="quant-citation-drawer-body">
				<div className="quant-citation-chart-container">
					<DynamicChartRender
						chartsResponse={chartsResponse}
						isChartView={isChartView}
						setIsChartView={setIsChartView}
						setChartData={() => {}}
						setIsChartLoading={() => {}}
						isAISummaryAvailable={false}
						renderedFrom={ChartRenderedFromType.AskKural}
					/>
				</div>
			</div>
		</div>
	);
};

export default QuantCitationDrawer;
